
const steps = [
  {
    number: '01',
    icon: 'ri-search-eye-line',
    label: 'STEP 1',
    title: '無料診断',
    subtitle: '物件ポテンシャルを見える化',
    description: '物件情報をお送りいただくだけで、AIと専門家が収益シミュレーションを作成。最適なプランをご提案するための土台を整えます。',
    duration: '最短24時間以内',
    color: 'emerald',
    highlights: ['収益シミュレーション作成', '競合物件リサーチ', '稼働率予測レポート'],
  },
  {
    number: '02',
    icon: 'ri-file-list-3-line',
    label: 'STEP 2',
    title: 'プラン提案',
    subtitle: '最適プランをオーダーメイド',
    description: '診断結果をもとに、物件・目標収益・オーナー様のご要望に合わせたカスタムプランをご提案。料金・サービス内容を丁寧にご説明します。',
    duration: '無料相談（60分）',
    color: 'amber',
    highlights: ['カスタムプラン提案', '料金・契約内容の説明', 'Q&Aセッション'],
  },
  {
    number: '03',
    icon: 'ri-rocket-2-line',
    label: 'STEP 3',
    title: '開業・運営開始',
    subtitle: '最短2週間でスタート',
    description: '許可申請・写真撮影・OTA登録・ゲスト対応まで、すべてをワンストップで代行。オーナー様は収益を受け取るだけのシンプルな運営が実現します。',
    duration: '最短2週間で開業',
    color: 'rose',
    highlights: ['許可申請・届出代行', 'OTA一括登録', '24時間ゲスト対応'],
  },
];

const colorMap: Record<string, { bg: string; border: string; text: string; badge: string; dot: string; iconBg: string; highlight: string }> = {
  emerald: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    text: 'text-emerald-600',
    badge: 'bg-emerald-100 text-emerald-700',
    dot: 'bg-emerald-400',
    iconBg: 'bg-emerald-500',
    highlight: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  },
  amber: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    text: 'text-amber-600',
    badge: 'bg-amber-100 text-amber-700',
    dot: 'bg-amber-400',
    iconBg: 'bg-amber-500',
    highlight: 'bg-amber-50 text-amber-700 border-amber-200',
  },
  rose: {
    bg: 'bg-rose-50',
    border: 'border-rose-200',
    text: 'text-rose-600',
    badge: 'bg-rose-100 text-rose-700',
    dot: 'bg-rose-400',
    iconBg: 'bg-rose-500',
    highlight: 'bg-rose-50 text-rose-700 border-rose-200',
  },
};

function StepFlowSection() {
  return (
    <section className="py-20 bg-gradient-to-b from-slate-50 to-white overflow-hidden">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">

        {/* Header */}
        <div className="text-center mb-16">
          <div className="inline-flex items-center gap-2 px-4 py-2 bg-emerald-100 text-emerald-700 rounded-full text-sm font-medium mb-5">
            <i className="ri-map-pin-2-line"></i>
            開業までの全体像
          </div>
          <h2 className="text-4xl lg:text-5xl font-bold text-slate-900 mb-4 tracking-tight">
            診断から開業まで、<br />
            <span className="text-emerald-500">3ステップで完結</span>
          </h2>
          <p className="text-lg text-slate-500 max-w-2xl mx-auto leading-relaxed">
            複雑な手続きや判断はすべてプロにお任せ。<br />
            オーナー様は「診断を受ける」だけで、あとは私たちが動きます。
          </p>
        </div>

        {/* Steps */}
        <div className="relative">
          {/* Connector line (desktop) */}
          <div className="hidden lg:block absolute top-16 left-1/2 -translate-x-1/2 w-[72%] h-0.5 bg-gradient-to-r from-emerald-300 via-amber-300 to-rose-300 z-0"></div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8 relative z-10">
            {steps.map((step, index) => {
              const c = colorMap[step.color];
              return (
                <div key={step.number} className="flex flex-col items-center text-center group">

                  {/* Icon circle */}
                  <div className={`relative w-16 h-16 flex items-center justify-center rounded-full ${c.iconBg} shadow-lg mb-6 group-hover:scale-110 transition-transform duration-300`}>
                    <i className={`${step.icon} text-2xl text-white`}></i>
                    {/* Step number badge */}
                    <span className="absolute -top-2 -right-2 w-6 h-6 flex items-center justify-center bg-white border-2 border-slate-200 rounded-full text-xs font-bold text-slate-600 shadow-sm">
                      {index + 1}
                    </span>
                  </div>

                  {/* Card */}
                  <div className={`w-full rounded-2xl border ${c.border} ${c.bg} p-7 shadow-sm hover:shadow-md transition-shadow duration-300`}>
                    {/* Label + duration */}
                    <div className="flex items-center justify-between mb-3">
                      <span className={`text-xs font-bold tracking-widest ${c.text}`}>{step.label}</span>
                      <span className={`text-xs px-2.5 py-1 rounded-full font-medium border ${c.highlight}`}>
                        <i className="ri-time-line mr-1"></i>{step.duration}
                      </span>
                    </div>

                    {/* Title */}
                    <h3 className="text-2xl font-bold text-slate-900 mb-1">{step.title}</h3>
                    <p className={`text-sm font-semibold ${c.text} mb-4`}>{step.subtitle}</p>

                    {/* Description */}
                    <p className="text-sm text-slate-600 leading-relaxed mb-5">{step.description}</p>

                    {/* Highlights */}
                    <ul className="space-y-2">
                      {step.highlights.map((h) => (
                        <li key={h} className="flex items-center gap-2 text-sm text-slate-700">
                          <span className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${c.dot}`}></span>
                          {h}
                        </li>
                      ))}
                    </ul>
                  </div>

                  {/* Arrow between steps (mobile) */}
                  {index < steps.length - 1 && (
                    <div className="lg:hidden mt-4 mb-2 text-slate-300">
                      <i className="ri-arrow-down-line text-2xl"></i>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Bottom CTA */}
        <div className="mt-14 text-center">
          <p className="text-slate-500 text-sm mb-5">まずは無料診断から。費用は一切かかりません。</p>
          <a
            href="/pricing#diagnosis"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gradient-to-r from-emerald-500 to-emerald-600 text-white font-semibold rounded-lg shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-search-eye-line text-lg"></i>
            無料診断を受ける（STEP 1へ）
          </a>
        </div>

      </div>
    </section>
  );
}

export default StepFlowSection;
